import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { Col, Row } from 'react-bootstrap'
import { State } from '@hookstate/core'
import { EMPTY_LINK, Link, useExhibitorDataState } from '../../globalStates/ExhibitorDataState'
import { useLanguageState } from '../../globalStates/LanguageState'
import { withReferenceDataState } from '../../globalStates/ReferenceDataHOC';
import { ReferenceDataState } from '../../globalStates/ReferenceDataState';
import { useConfigDataState } from '../../globalStates/ConfigDataState'
import { addElement, removeElementById } from '../../globalStates/utils'
import { newId } from '../../utils/Id'
import { repositionItem } from '../../utils/Arrays'
import { useLanguageSwitch } from '../../utils/Language'
import Page from '../../components/Page'
import Button from '../../components/Button';
import LanguageSwitch from '../languageSwitch'
import LinkSection from './LinkSection'

const ButtonsBar = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-end;
`

function sortLinks(linkStates: State<Link[]>): State<Link>[] {
    const links = linkStates.map(linkState => linkState)
    return links.sort((l1, l2) => l1.order.get() - l2.order.get())
}

interface LinksPageProps {
    refDataState: ReferenceDataState
}

const LinksPage: React.FC<LinksPageProps> = ({ refDataState }) => {
    const branding = useLanguageState().getStrings()
    const serviceKeys = useConfigDataState().getServiceKeys()
    const linksLimit = branding.configuration.monoLingual ? serviceKeys.link! : serviceKeys.link || 0
    const linksStates = useExhibitorDataState().getLinks()
    const orderedLinkStates = useMemo(() => sortLinks(linksStates), [linksStates])
    const isMonoLingual = branding.configuration.monoLingual ? 'De' : 'En'
    const { currentLanguage, onChangeLanguageHandler } = useLanguageSwitch(isMonoLingual);
    const monoLingual = branding.configuration.monoLingual
    const canAddLink = useMemo(() => {
        if (linksStates.length >= linksLimit) return false
        let valid = true;
        linksStates.forEach(linkState => {
            const text = linkState.text.get()
            const textDe = linkState.textDe.get()
            monoLingual ? valid = valid && textDe.length >= 0 : valid = valid && text.length >= 0
        });
        return valid
    }, [linksLimit, linksStates, monoLingual])

    const onAddLink = useCallback(() => {
        addElement<Link>(linksStates, { ...EMPTY_LINK, id: newId().toString(), order: linksStates.length + 1 })
    }, [linksStates])

    const onRemoveLink = useCallback((linkId: string) => {
        removeElementById<Link>(linksStates, linkId)
    }, [linksStates])

    const onOrderChanged = useCallback((linkId: string, newIndex: number) => {
        const oldIndex = orderedLinkStates.findIndex((linkState) => linkState.id.get() === linkId)
        const repositionedLinkStates = repositionItem(orderedLinkStates, orderedLinkStates[oldIndex], oldIndex, newIndex)
        repositionedLinkStates.forEach((link, index) => {
            if (link.order.get() !== index + 1) link.order.set(index + 1)
        })
    }, [orderedLinkStates])

    return (
        <Page
            title={branding.configuration.linksPage.pageTitle}
            description={branding.configuration.linksPage.pageDescription}>

            <Row>
                <Col>
                    <LanguageSwitch
                        id={`links_language_switch`}
                        name={`links_languages`}
                        value={currentLanguage}
                        onChange={onChangeLanguageHandler}
                        entityState={linksStates}
                    />
                </Col>
            </Row>

            {orderedLinkStates?.map((linkState, index) => (<LinkSection key={linkState.id.get()} linkState={linkState} index={index} currentLanguage={currentLanguage} onOrderChanged={onOrderChanged} onClickRemove={onRemoveLink} />))}

            <ButtonsBar>
                <Button id='addLink' caption={branding.configuration.linksPage.btnAddLink} inverse onClick={onAddLink} disabled={!canAddLink} />
            </ButtonsBar>
        </Page >
    );
}

export default withReferenceDataState(LinksPage)